import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format, startOfYear, endOfYear, addDays, differenceInCalendarDays } from 'date-fns';
import { CalendarBlank } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WEEK_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const FULL_MINUTES = 45;

/** GitHub-style contribution grid for the CURRENT calendar year with exactly
 *  one square per day. Opacity scales with the total workout minutes for that
 *  day (45 min = full).
 */
export default function ActivityHeatmap({ year }) {
  const currentYear = year || new Date().getFullYear();
  const [byDate, setByDate] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/progress/activity-heatmap?year=${currentYear}`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        const map = {};
        (data.days || []).forEach((d) => { map[d.date] = d; });
        setByDate(map);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [currentYear]);

  // Build week-columns for the entire year, one row per weekday (Mon..Sun).
  const { columns, monthLabels, totalWorkouts } = useMemo(() => {
    const yearStart = startOfYear(new Date(currentYear, 0, 1));
    const yearEnd = endOfYear(yearStart);
    // Start column at the Monday on or before Jan 1
    const startDow = (yearStart.getDay() + 6) % 7; // 0=Mon
    const firstColStart = addDays(yearStart, -startDow);
    const totalDays = differenceInCalendarDays(yearEnd, firstColStart) + 1;
    const totalWeeks = Math.ceil(totalDays / 7);

    const cols = [];
    const mLabels = [];
    let lastMonth = -1;
    let workouts = 0;

    for (let w = 0; w < totalWeeks; w += 1) {
      const week = [];
      for (let d = 0; d < 7; d += 1) {
        const dt = addDays(firstColStart, w * 7 + d);
        const inYear = dt.getFullYear() === currentYear;
        const key = format(dt, 'yyyy-MM-dd');
        const entry = byDate[key];
        const minutes = inYear ? (entry?.duration_minutes || 0) : 0;
        const count = inYear ? (entry?.count || 0) : 0;
        if (count > 0) workouts += 1;
        week.push({
          date: dt,
          key,
          inYear,
          minutes,
          count,
        });
      }
      cols.push(week);
      // Month label — placed on the first week of each month
      const firstDayOfWeek = week[0].date;
      if (firstDayOfWeek.getFullYear() === currentYear && firstDayOfWeek.getMonth() !== lastMonth) {
        mLabels.push({ col: w, label: format(firstDayOfWeek, 'MMM') });
        lastMonth = firstDayOfWeek.getMonth();
      }
    }
    return { columns: cols, monthLabels: mLabels, totalWorkouts: workouts };
  }, [byDate, currentYear]);

  if (loading) {
    return (
      <div className="ios-card p-4 mb-4">
        <div className="animate-pulse-soft text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/40">
          Loading activity…
        </div>
      </div>
    );
  }

  return (
    <div className="ios-card p-4 mb-4" data-testid="activity-heatmap-year">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} weight="fill" className="text-userRed" />
          <h2 className="text-lg font-black font-heading uppercase text-graphite">Activity</h2>
        </div>
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50" data-testid="heatmap-year-total">
          {totalWorkouts} workouts · {currentYear}
        </p>
      </div>

      {/* Scrollable container so all 53 columns fit on narrow screens */}
      <div className="overflow-x-auto no-scrollbar -mx-1 px-1">
        <div className="inline-block min-w-full">
          {/* Month labels */}
          <div className="grid" style={{ gridTemplateColumns: `12px repeat(${columns.length}, 12px)`, gap: '2px', marginBottom: '4px' }}>
            <span />
            {columns.map((_, i) => {
              const lbl = monthLabels.find((m) => m.col === i);
              return (
                <span
                  key={i}
                  className="text-[8px] font-black uppercase tracking-[0.05em] text-graphite/50 leading-none"
                  style={{ writingMode: 'horizontal-tb' }}
                >
                  {lbl ? lbl.label : ''}
                </span>
              );
            })}
          </div>

          {/* Grid: 7 rows × N columns */}
          <div className="grid" style={{ gridTemplateColumns: `12px repeat(${columns.length}, 12px)`, gap: '2px' }}>
            {/* Weekday labels down the left */}
            {[0, 1, 2, 3, 4, 5, 6].map((row) => (
              <React.Fragment key={row}>
                <span className="text-[8px] font-black uppercase text-graphite/40 text-right leading-[12px] pr-0.5">
                  {row % 2 === 0 ? WEEK_LABELS[row] : ''}
                </span>
                {columns.map((week, ci) => {
                  const cell = week[row];
                  if (!cell.inYear) {
                    return <span key={ci} style={{ width: 12, height: 12, opacity: 0.15 }} className="rounded-[2px] bg-graphite/10" />;
                  }
                  const o = Math.min(1, (cell.minutes || 0) / FULL_MINUTES);
                  const bg = o > 0
                    ? `rgba(192, 57, 47, ${Math.max(0.18, o).toFixed(2)})`
                    : 'rgba(25,28,33,0.09)';
                  return (
                    <span
                      key={ci}
                      title={`${format(cell.date, 'MMM d')} · ${Math.round(cell.minutes)} min`}
                      style={{ width: 12, height: 12, backgroundColor: bg }}
                      className="rounded-[2px]"
                      data-testid={`year-cell-${cell.key}`}
                      data-minutes={Math.round(cell.minutes)}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-1.5 mt-3">
        <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-graphite/50">Less</span>
        {[0.12, 0.35, 0.6, 0.85, 1].map((o, i) => (
          <span key={i} style={{ width: 10, height: 10, backgroundColor: `rgba(192, 57, 47, ${o})` }} className="rounded-[2px]" />
        ))}
        <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-graphite/50">More</span>
        <span className="ml-auto text-[9px] uppercase tracking-[0.15em] font-bold text-graphite/40">
          45 min = full
        </span>
      </div>
    </div>
  );
}
